import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterBar, type FilterConfig } from "@/components/admin/shared";
import {
  ORDER_STATUSES,
  ORDER_STATUS_I18N_KEYS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_I18N_KEYS,
} from "@/lib/admin-api/orders";
import { useRegions } from "@/hooks/admin/useRegions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrderFilterValues {
  readonly status: string | null;
  readonly payment_method: string | null;
  readonly region_id: string | null;
}

export interface OrderFiltersProps {
  readonly values: OrderFilterValues;
  readonly onChange: (key: string, value: unknown) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderFilters({ values, onChange }: OrderFiltersProps) {
  const { t } = useTranslation("admin");
  const { data: regionsData } = useRegions();

  const filters: readonly FilterConfig[] = useMemo(() => {
    const statusOptions = ORDER_STATUSES.map((status) => ({
      value: status,
      label: t(ORDER_STATUS_I18N_KEYS[status]),
    }));

    const paymentOptions = PAYMENT_METHODS.map((method) => ({
      value: method,
      label: t(PAYMENT_METHOD_I18N_KEYS[method]),
    }));

    const regionOptions =
      regionsData?.regions
        .filter((r) => r.active)
        .map((r) => ({ value: r.id, label: r.name })) ?? [];

    return [
      {
        key: "status",
        label: t("orders.filter_status", "Statut"),
        type: "status",
        options: statusOptions,
      },
      {
        key: "payment_method",
        label: t("orders.filter_payment", "Paiement"),
        type: "select",
        options: paymentOptions,
      },
      {
        key: "region_id",
        label: t("orders.filter_region", "Région"),
        type: "select",
        options: regionOptions,
      },
    ];
  }, [t, regionsData]);

  return (
    <FilterBar
      filters={filters}
      values={values as unknown as Readonly<Record<string, unknown>>}
      onChange={onChange}
    />
  );
}
